import React, { useState, useCallback } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { Modal as UIModal } from "@/components/ui/Modal";
import { Label } from "@/components/ui/Label";
import { SuccessState } from "@/components/ui/SuccessState";
import { ErrorState } from "@/components/ui/ErrorState";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import type { ApprovalChecklistItem } from "@/lib/api/review";
import { APPROVAL_CHECKLIST_ITEMS } from "@/lib/api/review";
import { cn } from "@/lib/utils";

const APPROVE_CHECKLIST_KEYS = [
  "review.review.decision.approveModal.checklist.legalIdentity",
  "review.review.decision.approveModal.checklist.businessRegistration",
  "review.review.decision.approveModal.checklist.complianceCertificates",
  "review.review.decision.approveModal.checklist.financialSettlement",
  "review.review.decision.approveModal.checklist.brandAssets",
] as const satisfies TranslationKeys[];

interface ApproveConfirmModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
  onSubmit: (checklist: ApprovalChecklistItem[]) => Promise<void>;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
}

type ApprovalState = "form" | "success" | "error";

export function ApproveConfirmModal({
  visible,
  onDismiss,
  onConfirm,
  isSubmitting,
  onSubmit,
  t,
}: ApproveConfirmModalProps) {
  const [checked, setChecked] = useState<boolean[]>(
    Array(APPROVE_CHECKLIST_KEYS.length).fill(false)
  );
  const [state, setState] = useState<ApprovalState>("form");
  const [error, setError] = useState<TranslationKeys | null>(null);

  const allChecked = checked.every(Boolean);

  const resetForm = useCallback(() => {
    setState("form");
    setChecked(Array(APPROVE_CHECKLIST_KEYS.length).fill(false));
    setError(null);
  }, []);

  const handleDismiss = useCallback(() => {
    resetForm();
    onDismiss();
    if (state === "success") {
      onConfirm();
    }
  }, [resetForm, onDismiss, onConfirm, state]);

  const handleConfirm = useCallback(async () => {
    setError(null);
    try {
      await onSubmit(APPROVAL_CHECKLIST_ITEMS);
      setState("success");
    } catch {
      setError("common.error.generic");
      setState("error");
    }
  }, [onSubmit]);

  return (
    <UIModal
      visible={visible}
      onDismiss={handleDismiss}
      title={state === "form" ? "review.review.decision.approve" : null}
      closeOnBackdrop={false}
      useScrollView
      footer={
        state === "form" ? (
          <View className="flex-row items-center justify-end gap-4">
            <Button label="common.cancel" variant="tertiary" onPress={handleDismiss} />
            <Button
              label="review.review.decision.approveModal.confirmApproval"
              variant="primary"
              disabled={!allChecked || isSubmitting}
              loading={isSubmitting}
              onPress={handleConfirm}
            />
          </View>
        ) : null
      }
    >
      {state === "form" && (
        <>
          {/* Verification Checklist label */}
          <Label
            required
            label="review.review.decision.approveModal.verificationChecklist"
            className="text-supporting-text-40 mb-4"
          />

          {/* Checklist items */}
          <View className="gap-4 items-start w-full">
            {APPROVE_CHECKLIST_KEYS.map((key, i) => (
              <View
                key={key}
                className={cn(
                  "w-full items-start border rounded-xl p-4",
                  checked[i] ? "bg-green-5 border-green-20" : "bg-stroke-10 border-stroke-50"
                )}
              >
                <Checkbox
                  checked={checked[i]}
                  onChange={(val) =>
                    setChecked((prev) => prev.map((v, idx) => (idx === i ? val : v)))
                  }
                  label={key}
                  variant="success"
                  className="w-full"
                />
              </View>
            ))}
          </View>

          {/* Note */}
          {!allChecked && (
            <AppText className="text-sm text-supporting-text-40 mt-4 italic">
              {t("review.review.decision.approveModal.checklistNote")}
            </AppText>
          )}
        </>
      )}

      {state === "success" && (
        <SuccessState
          title="review.review.decision.approveModal.successTitle"
          description="review.review.decision.approveModal.successDescription"
          className="px-0 pt-0"
        />
      )}

      {state === "error" && (
        <ErrorState
          description={error || "common.error.generic"}
          className="px-0 pt-0"
          onRetry={() => {
            setState("form");
            setError(null);
          }}
        />
      )}
    </UIModal>
  );
}
